import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { DarkLogo } from "../Common/assets/Sidebar";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { canNavigateForm } from "../../Redux/actions/responseAction";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import http from "../../utils/http";
import { setParticipantLang } from "../../Redux/reducers/responseSlice";
import { Menu } from "@mui/icons-material";
import { ScreeningMenuDrawer } from "./ScreeningMenuDrawer";

export const ScreeningHeader = ({ languages, refreshPage }: any) => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width:900px)");
  const isFieldSubmitting = useAppSelector(
    (state) => state.response.fieldSubmitting
  );
  const isFormLoading = useAppSelector((state) => state.response.isLoading);
  const lang = useAppSelector((state) => state.response.lang);
  const [showDrawer, setShowDrawer] = useState(false);

  const handleLanguageChange = (e: any) => {
    const selectedLang = e.target.value;
    if (selectedLang !== "en") {
      dispatch(setParticipantLang(selectedLang));
    } else {
      dispatch(setParticipantLang(""));
    }
    localStorage.setItem("lang", selectedLang);
    refreshPage();
  };

  const closeDrawer = () => {
    setShowDrawer(false);
  };

  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      sx={{
        height: "70px",
        px: 3,
        py: 2,
        border: { xs: "1px solid #E5E7EB", md: "none" },
        "& .header-logo": {
          maxWidth: "258px",
          maxHeight: "60px",
        },
        backgroundColor: "#FFF",
      }}
      gap={3}
    >
      {isMobile && languages.length > 1 && (
        <IconButton
          aria-label="menu"
          size="large"
          onClick={() => setShowDrawer(true)}
        >
          <Menu fontSize="inherit" />
        </IconButton>
      )}
      <Box
        sx={{
          width: "120px",
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <DarkLogo />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isFieldSubmitting && <CircularProgress color="primary" size={24} />}
        </Box>
        {!isMobile && !isFormLoading && languages.length > 1 && (
          <Select
            value={lang || "en"}
            onChange={handleLanguageChange}
            id="lang"
            sx={{ height: "48px" }}
          >
            {languages.map((lang: any) => (
              <MenuItem key={lang.languageCode} value={lang.languageCode}>
                {lang?.languageLabel}
              </MenuItem>
            ))}
          </Select>
        )}
        <ScreenerFinish />
      </Box>

      {showDrawer && (
        <ScreeningMenuDrawer
          showDrawer={showDrawer}
          onClose={closeDrawer}
          isMobile={isMobile}
          languages={languages}
          lang={lang}
          handleLanguageChange={handleLanguageChange}
        />
      )}
    </Stack>
  );
};

type ConfirmProps = {
  show: boolean;
  onClose: () => void;
};
const ConfirmModal = ({ show, onClose }: ConfirmProps) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const { id: studyId } = useParams();
  const attemptId = useAppSelector(
    (state) => state.response.screeningAttemptId
  );

  const navigate = useNavigate();
  const handleOk = async () => {
    try {
      setSubmitLoader(true);
      await http.post(
        `/study/${studyId}/participant-screening-responses/attempt/${attemptId}/finish`
      );
      navigate("/thank-you");
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Dialog
      open={show}
      onClose={onClose}
      PaperProps={{ sx: { padding: "8px" } }}
    >
      <DialogTitle>Confirm Survey Submission?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Confirm you want to submit your responses?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {!submitLoader ? (
          <>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleOk} autoFocus>
              Confirm
            </Button>
          </>
        ) : (
          <CircularProgress size={20} sx={{ mr: 1 }} />
        )}
      </DialogActions>
    </Dialog>
  );
};
export const ScreenerFinish = () => {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const isFormLoading = useAppSelector((state) => state.response.isLoading);
  const onClose = () => {
    setShowModal(false);
  };
  const handleNext = () => {
    const canNavigate = dispatch(canNavigateForm());
    if (!canNavigate) {
      toastMessage(
        "warning",
        "Please complete all the required questions before finishing."
      );
      return;
    }
    setShowModal(true);
  };

  if (isFormLoading) {
    return null;
  }
  return (
    <>
      <Button variant="contained" onClick={handleNext}>
        Finish
      </Button>
      {showModal && <ConfirmModal show={showModal} onClose={onClose} />}
    </>
  );
};
